import { ComputedRef, watch } from 'vue'

import { UseEventClock, useEventClock } from '@collector/shared-ui-domain'
import { getMinuteWithSecond, MinuteWithSecond } from '@collector/shared-utils'
import { ClockStatus } from '@collector/sportsapi-client-legacy'
import { useEvent, useEventPeriod } from '@desktop/views/Relation/composables'

import { GetEventClockSeconds, GetEventClockTime } from '../../../configuration'

type Params = {
  currentPeriodLengthMinutes: ComputedRef<number | null>
  currentPeriodStartOffsetMinutes: ComputedRef<number | null>
  extraTimePlayed?: ComputedRef<boolean>
  getEventClockSeconds: GetEventClockSeconds
  getEventClockTime: GetEventClockTime
  isTimerDirectionDown: ComputedRef<boolean>
  maxMinute?: ComputedRef<number>
}

export function useInitEventClock({
  currentPeriodLengthMinutes,
  currentPeriodStartOffsetMinutes,
  extraTimePlayed,
  isTimerDirectionDown,
  getEventClockSeconds,
  getEventClockTime,
  maxMinute,
}: Params): UseEventClock {
  const { event, eventTimestamp } = useEvent()
  const { periodLengthMinutes, extraPeriodLengthMinutes } = useEventPeriod()
  const eventClock = useEventClock({
    clockRunning: isEventClockStatusRunning(),
    isTimerDirectionDown,
    maxMinute,
  })

  function isEventClockStatusRunning(): boolean {
    return event.value.clock_status === ClockStatus.Running
  }

  function getClockMinuteWithSecond(clockTime: number): MinuteWithSecond {
    const eventClockSeconds = getEventClockSeconds(
      clockTime,
      currentPeriodLengthMinutes.value,
      periodLengthMinutes.value,
      extraPeriodLengthMinutes.value,
      isTimerDirectionDown.value,
      event.value.status_id,
      extraTimePlayed?.value,
    )

    return getMinuteWithSecond(eventClockSeconds)
  }

  function setMinuteSecond(clockTime: number): void {
    const { minute, second } = getClockMinuteWithSecond(clockTime)

    eventClock.setMinuteSecond(minute, second)
  }

  function getEventClockDelay(): number {
    return isEventClockStatusRunning()
      ? Math.round((Date.now() - eventTimestamp.value.localSyncTime) / 1000)
      : 0
  }

  const { minute, second } = getClockMinuteWithSecond(
    (event.value.clock_time || 0) + getEventClockDelay(),
  )
  eventClock.initTimer(minute, second)

  watch(isTimerDirectionDown, (_, oldValue) => {
    const clockTime = getEventClockTime(
      eventClock.minute.value,
      eventClock.second.value,
      currentPeriodStartOffsetMinutes.value,
      oldValue,
    )

    setMinuteSecond(clockTime)
  })

  function resyncClockDisplayForCurrentPeriodMetadata(): void {
    const clockTime = getEventClockTime(
      eventClock.minute.value,
      eventClock.second.value,
      currentPeriodStartOffsetMinutes.value,
      isTimerDirectionDown.value,
    )

    setMinuteSecond(clockTime)
  }

  watch(
    [currentPeriodLengthMinutes, currentPeriodStartOffsetMinutes],
    resyncClockDisplayForCurrentPeriodMetadata,
  )

  watch(
    () => event.value.status_id,
    () => {
      const eventClockStatusRunning = isEventClockStatusRunning()

      if (
        event.value.clock_time === 0 ||
        eventClockStatusRunning !== eventClock.isClockRunning.value
      ) {
        const { minute, second } = getClockMinuteWithSecond(
          event.value.clock_time || 0,
        )
        eventClock.restartClock(eventClockStatusRunning, minute, second)
      }
    },
  )

  watch(
    [() => event.value.status_id, () => event.value.clock_status],
    ([newEventStatusId], [oldEventStatusId]) => {
      const eventClockStatusRunning = isEventClockStatusRunning()

      if (newEventStatusId !== oldEventStatusId) {
        if (
          event.value.clock_time === 0 ||
          eventClockStatusRunning !== eventClock.isClockRunning.value
        ) {
          const { minute, second } = getClockMinuteWithSecond(
            event.value.clock_time || 0,
          )
          eventClock.restartClock(eventClockStatusRunning, minute, second)
        }
      } else if (eventClockStatusRunning !== eventClock.isClockRunning.value) {
        if (eventClockStatusRunning) eventClock.startClock()
        else eventClock.stopClock()
      }
    },
  )

  return eventClock
}
